import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAuctionDetails, clearAuctionDetails } from "../../../redux/slices/auctionsSlice";
import AllLots from "../../../components/Auctions/Lots/AllLots";
import TitleBar from "../../../components/TitleBar";
import { useTranslation } from "../../../contexts/LanguageContext";

function ViewAuction() {
  const { auctionId } = useParams();
  const dispatch = useDispatch();
  const { auctionDetails, loadingDetails, errorDetails } = useSelector((state) => state.auctions);
  const { t, tStatus } = useTranslation();

  useEffect(() => {
    if (auctionId) {
      dispatch(fetchAuctionDetails(auctionId));
    }
    return () => dispatch(clearAuctionDetails());
  }, [dispatch, auctionId]);

  // Format date range
  const formatDateRange = (startTime, endTime) => {
    if (!startTime || !endTime) return "N/A";
    const start = new Date(startTime);
    const end = new Date(endTime);
    return `${start.toLocaleDateString()} to ${end.toLocaleDateString()}`;
  };

  if (loadingDetails) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3">{t("auctionDetail.loading")}</span>
      </div>
    );
  }

  if (errorDetails) {
    return (
      <div className="text-center py-16">
        <p className="text-red-500 text-lg">{errorDetails}</p>
      </div>
    );
  }

  if (!auctionDetails) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500 text-lg">{t("auctionDetail.notFound")}</p>
      </div>
    );
  }

  const auction = auctionDetails.data || auctionDetails;

  return (
    <div>
      <div>
        <TitleBar title={t("auctionDetail.title")} />
      </div>
      <div className="mt-4"></div>
      <div className="px-4 sm:px-4 lg:px-4 bg-white rounded-lg overflow-hidden">
        <div className="items-center py-2 border-b border-gray-300">
          <div className="md:flex">
            <h3 className="font-medium text-lg mr-6">
              {t("auctionDetail.auctionId")} <span className="font-light">{auction._id?.slice(-6) || 'N/A'}</span>
            </h3>
            <h3 className="font-medium text-lg">
              {t("auctionDetail.title2")} <span className="font-light">{auction.title || t("auctionDetail.untitled")}</span>
            </h3>
          </div>
        </div>
        <div className="grid grid-cols-4 py-3 gap-10 gap-y-4">
          <div>
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">{t("auctionDetail.sellerName")}</h4>
              <span className="text-sm font-light">
                {auction.seller?.name || auction.userId?.name || t("auctionDetail.unknownSeller")}
              </span>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">{t("auctionDetail.country")}</h4>
              <span className="text-sm font-light">
                {auction.country?.countryName || 'N/A'}
              </span>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">{t("auctionDetail.duration")}</h4>
              <span className="text-sm font-light">
                {formatDateRange(auction.startTime, auction.endTime)}
              </span>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">{t("auctionDetail.type")}</h4>
              <span className="text-sm font-light">
                {auction.bidderType || t("auctionDetail.standard")}
              </span>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">{t("auctionDetail.totalBids")}</h4>
              <span className="text-sm font-light">
                {auction.totalBids || 0}
              </span>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">{t("auctionDetail.totalLots")}</h4>
              <span className="text-sm font-light">
                {auction.lotIds?.length || auction.lots?.length || 0}
              </span>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">{t("auctionDetail.entryAmount")}</h4>
              <span className="text-sm font-light">
                {auction.entryAmount ? `${auction.entryAmount} KD` : t("auctionDetail.free")}
              </span>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">{t("auctionDetail.status")}</h4>
              <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                auction.status === 'live' ? 'bg-green-100 text-green-800' :
                auction.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                auction.status === 'blocked' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {tStatus(auction.status)}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4"></div>
      <AllLots auctionId={auctionId} />
    </div>
  );
}

export default ViewAuction;
